/**
 * Menu lateral do app, igual em todas as telas, na camada Ops-Next: fundo
 * claro, borda fina à direita e o acento só no item ativo. Itens, ordem,
 * nomes e permissões vêm de `appMenu.ts` (a mesma fonte do breadcrumb e da
 * paleta "Ir para…"); o item ativo é a seção que o breadcrumb mostra.
 *
 * Aberto (232 px) com rótulos, seções e grupos que abrem (o grupo da página
 * atual abre sozinho); recolhido (56 px) só com ícones, com dica ao passar o
 * mouse ou com o foco (Esc esconde), e os grupos viram os itens deles. A
 * escolha fica por pessoa (`useAsideCollapsed`). No celular/tablet é o drawer
 * do Metronic (pelo id e pelos `data-kt-drawer`), aberto pelo botão do header
 * e sempre com rótulos.
 */
import {
  FC,
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Link, useLocation} from 'react-router-dom';
import clsx from 'clsx';

import {useMyPendingCount} from 'app/modules/tickets/hooks/useMyPendingCount';
import useRouteRedirection from 'app/shared/hooks/useRouteRedirection';

import {DrawerComponent} from '../../../assets/ts/components';
import {toAbsoluteUrl} from '../../../helpers';
import {usePageData} from '../../core';
import {resolveSection} from '../app-header/breadcrumbTrail';
import {
  AppMenuGroup,
  AppMenuItem,
  asideMenuTree,
  flattenMenu,
  useAppMenuTree,
} from './appMenu';
import './appAside.scss';

const DESKTOP_QUERY = '(min-width: 992px)';

const matchesDesktop = () =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? true
    : window.matchMedia(DESKTOP_QUERY).matches;

/** Só no desktop o menu recolhe; no drawer do celular ele é sempre aberto. */
const useIsDesktop = () => {
  const [desktop, setDesktop] = useState(matchesDesktop);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(query.matches);
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) {
        query.removeEventListener('change', onChange);
      } else query.removeListener(onChange);
    };
  }, []);
  return desktop;
};

const pendingText = (count: number) =>
  `${count} ${count === 1 ? 'precisa' : 'precisam'} da sua ação`;

type Tip = {text: string; top: number; left: number};

type Props = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

const AppAside: FC<Props> = ({collapsed, onToggleCollapsed}) => {
  const {pathname} = useLocation();
  const tree = useAppMenuTree();
  // O item ativo vem do menu inteiro (o breadcrumb também); o aside desenha
  // sem os itens que só a paleta lista
  const menu = useMemo(() => flattenMenu(tree), [tree]);
  const drawn = useMemo(() => asideMenuTree(tree), [tree]);
  const {pageTitle, pageHeaderNavigation} = usePageData();
  const pendingCount = useMyPendingCount() ?? 0;
  const {routeRedirectionByUser} = useRouteRedirection();
  const desktop = useIsDesktop();
  const iconsOnly = collapsed && desktop;

  // Ativo: a seção da página, a mesma do breadcrumb
  const activeTo = resolveSection(
    pathname,
    menu,
    pageHeaderNavigation,
    pageTitle
  )?.to;
  const activeGroupId = useMemo(
    () =>
      tree
        .flatMap(({entries}) => entries)
        .flatMap((entry) => (entry.kind === 'group' ? [entry] : []))
        .find(({items}) => items.some(({to}) => to === activeTo))?.id,
    [tree, activeTo]
  );

  // O grupo da página atual abre sozinho; os outros ficam como a pessoa deixou
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroupId ? [activeGroupId] : [])
  );
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((previous) =>
      previous.has(activeGroupId)
        ? previous
        : new Set([...Array.from(previous), activeGroupId])
    );
  }, [activeGroupId]);

  const toggleGroup = (id: string) =>
    setOpenGroups((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Drawer do celular/tablet: fecha ao trocar de página
  useEffect(() => {
    const timer = window.setTimeout(
      () => DrawerComponent.reinitialization(),
      50
    );
    return () => window.clearTimeout(timer);
  }, [pathname]);

  // Dica do menu recolhido: fixa ao lado do item (o aside rola e cortaria)
  const [tip, setTip] = useState<Tip | null>(null);
  useEffect(() => {
    if (!iconsOnly) setTip(null);
  }, [iconsOnly]);
  const tipHandlers = (text: string) => {
    const show = (event: SyntheticEvent<HTMLElement>) => {
      if (!iconsOnly) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setTip({text, top: rect.top + rect.height / 2, left: rect.right + 8});
    };
    const hide = () => setTip(null);
    return {
      onMouseEnter: show,
      onFocus: show,
      onMouseLeave: hide,
      onBlur: hide,
    };
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && tip) setTip(null);
  };

  const renderItem = (item: AppMenuItem, nested = false) => {
    const active = item.to === activeTo;
    const count = item.ticketsBadge ? pendingCount : 0;
    return (
      <li key={item.to}>
        <Link
          to={item.to}
          className={clsx(
            'opx-aside__item',
            nested && 'is-nested',
            active && 'is-active'
          )}
          aria-current={
            active ? (pathname === item.to ? 'page' : 'true') : undefined
          }
          aria-label={
            count
              ? `${item.title}, ${pendingText(count)}`
              : iconsOnly
              ? item.title
              : undefined
          }
          {...tipHandlers(count ? `${item.title} · ${count}` : item.title)}
        >
          <i
            className={clsx('bi opx-aside__icon', item.fontIcon)}
            aria-hidden='true'
          />
          {!iconsOnly && <span className='opx-aside__label'>{item.title}</span>}
          {count > 0 &&
            (iconsOnly ? (
              <span className='opx-aside__dot' aria-hidden='true' />
            ) : (
              <span className='opx-aside__count' aria-hidden='true'>
                {count > 99 ? '99+' : count}
              </span>
            ))}
        </Link>
      </li>
    );
  };

  const renderGroup = (group: AppMenuGroup) => {
    // Recolhido: os itens do grupo, cada um com o seu ícone
    if (iconsOnly) return group.items.map((item) => renderItem(item));
    const open = openGroups.has(group.id);
    const listId = `opx-aside-${group.id}`;
    return (
      <li key={group.id}>
        <button
          type='button'
          className={clsx(
            'opx-aside__item opx-aside__group',
            group.items.some(({to}) => to === activeTo) && 'has-active'
          )}
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => toggleGroup(group.id)}
        >
          <i
            className={clsx('bi opx-aside__icon', group.fontIcon)}
            aria-hidden='true'
          />
          <span className='opx-aside__label'>{group.title}</span>
          <i
            className='bi bi-chevron-down opx-aside__chevron'
            aria-hidden='true'
          />
        </button>
        {open && (
          <ul id={listId} className='opx-aside__list opx-aside__sub'>
            {group.items.map((item) => renderItem(item, true))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div
      id='kt_aside'
      className={clsx('opx-app opx-aside', iconsOnly && 'is-collapsed')}
      data-kt-drawer='true'
      data-kt-drawer-name='aside'
      data-kt-drawer-activate='{default: true, lg: false}'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width='auto'
      data-kt-drawer-direction='start'
      data-kt-drawer-toggle='#kt_aside_toggle'
      onKeyDown={onKeyDown}
    >
      <div className='opx-aside__brand'>
        <Link
          to={routeRedirectionByUser('/quotes/workspace')}
          className='opx-aside__logo'
          {...tipHandlers('Página inicial')}
        >
          <img
            className={iconsOnly ? 'is-symbol' : 'is-wordmark'}
            src={toAbsoluteUrl(
              iconsOnly
                ? '/media/logos/mecanizou-logo-symbol.svg'
                : '/media/logos/mecanizou-logo.svg'
            )}
            alt='Mecanizou'
          />
        </Link>
      </div>

      <nav
        className='opx-aside__nav'
        aria-label='Menu principal'
        onScroll={() => setTip(null)}
      >
        {drawn.map((section) => {
          const headingId = `opx-aside-secao-${section.id}`;
          const showHeading = Boolean(section.title) && !iconsOnly;
          return (
            <div key={section.id} className='opx-aside__section'>
              {showHeading && (
                <span id={headingId} className='opx-aside__heading'>
                  {section.title}
                </span>
              )}
              <ul
                className='opx-aside__list'
                aria-labelledby={showHeading ? headingId : undefined}
                aria-label={
                  section.title && !showHeading ? section.title : undefined
                }
              >
                {section.entries.map((entry) =>
                  entry.kind === 'group'
                    ? renderGroup(entry)
                    : renderItem(entry)
                )}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className='opx-aside__foot'>
        <button
          type='button'
          className='opx-aside__item opx-aside__collapse'
          aria-label={iconsOnly ? 'Expandir menu' : 'Recolher menu'}
          onClick={() => {
            setTip(null);
            onToggleCollapsed();
          }}
          {...tipHandlers('Expandir')}
        >
          <i
            className={clsx(
              'bi opx-aside__icon',
              iconsOnly ? 'bi-chevron-bar-right' : 'bi-chevron-bar-left'
            )}
            aria-hidden='true'
          />
          {!iconsOnly && <span className='opx-aside__label'>Recolher</span>}
        </button>
      </div>

      {tip && (
        <span
          className='opx-aside__tip'
          style={{top: tip.top, left: tip.left}}
          aria-hidden='true'
        >
          {tip.text}
        </span>
      )}
    </div>
  );
};

export default AppAside;
